import { ref, type Ref } from 'vue';
import { useHeaderOffset } from '@/composables/useHeaderOffset';

export interface AnchoredMenuPlacement {
  /** Align menu with the start (left) of the trigger. Default is end (right-aligned). */
  start?: boolean;
  /** Menu opens above the trigger. */
  top?: boolean;
  /** Menu opens to the left of the trigger. */
  left?: boolean;
  /** Menu opens to the right of the trigger. */
  right?: boolean;
}

export interface AnchoredMenuStyle {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  maxHeight?: string;
}

const GAP = 4;
const VIEWPORT_MARGIN = 8;

/**
 * Positions a teleported, `position: fixed` menu against its trigger. Anchoring to the trigger
 * alone lets a trigger near the edge of the window push the menu off-screen, where it is clipped
 * rather than scrolled into view, so the vertical result is fitted to the viewport.
 *
 * The list must already be rendered when `updateMenuPosition` runs (call it in `nextTick` after
 * opening) — its measured height is what the fitting is based on.
 *
 * `capHeight` is opt-in because it only has an effect when the consumer also makes the list a
 * scroll container, and turning arbitrary popup content into one clips anything that escapes its
 * bounds. Plain lists of menu items want it; popups hosting richer content do not.
 */
export function useAnchoredMenuPosition(
  triggerRef: Ref<HTMLElement | null>,
  listRef: Ref<HTMLElement | null>,
  placement: AnchoredMenuPlacement,
  options: { capHeight?: boolean } = {},
) {
  const { totalOffset } = useHeaderOffset();
  const menuStyle = ref<AnchoredMenuStyle | null>(null);

  function updateMenuPosition() {
    const el = triggerRef.value;
    if (!el) return;
    const rect = el.getBoundingClientRect();

    const listHeight = listRef.value?.offsetHeight ?? 0;
    const minTop = totalOffset.value + VIEWPORT_MARGIN;
    const availableHeight = window.innerHeight - minTop - VIEWPORT_MARGIN;
    const fitTop = (top: number) => Math.min(Math.max(top, minTop), Math.max(minTop, window.innerHeight - listHeight - VIEWPORT_MARGIN));
    const fitBottom = (bottom: number) =>
      Math.min(Math.max(bottom, VIEWPORT_MARGIN), Math.max(VIEWPORT_MARGIN, window.innerHeight - minTop - listHeight));

    // Only cap a list that actually overruns the viewport, so menus that fit keep their natural
    // box and never become scroll containers.
    const heightCap = options.capHeight && listHeight > availableHeight ? { maxHeight: `${availableHeight}px` } : {};

    if (placement.top) {
      menuStyle.value = {
        bottom: `${fitBottom(window.innerHeight - rect.top + GAP)}px`,
        right: `${window.innerWidth - rect.right}px`,
        ...heightCap,
      };
    } else if (placement.left) {
      menuStyle.value = {
        top: `${fitTop(rect.top)}px`,
        right: `${window.innerWidth - rect.left + GAP}px`,
        ...heightCap,
      };
    } else if (placement.right) {
      menuStyle.value = {
        top: `${fitTop(rect.top)}px`,
        left: `${rect.right + GAP}px`,
        ...heightCap,
      };
    } else {
      menuStyle.value = {
        top: `${fitTop(rect.bottom + GAP)}px`,
        ...(placement.start ? { left: `${rect.left}px` } : { right: `${window.innerWidth - rect.right}px` }),
        ...heightCap,
      };
    }
  }

  return { menuStyle, updateMenuPosition };
}
